import { Shield, ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import { Badge } from '../ui';

interface ProofOfReserveBadgeProps {
  /** Coverage ratio (0-1) or percentage (0-100) */
  coverageRatio?: number;
  /** Coverage as percentage (0-100) - alias for coverageRatio */
  coverage?: number;
  lastVerified?: string;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function ProofOfReserveBadge({ 
  coverageRatio, 
  coverage,
  lastVerified, 
  showDetails = false,
  size = 'md' 
}: ProofOfReserveBadgeProps) {
  // Support both coverageRatio (0-1) and coverage (0-100)
  let percentage: number;
  if (coverage !== undefined) {
    percentage = coverage; // Already a percentage
  } else if (coverageRatio !== undefined) {
    percentage = coverageRatio <= 1 ? coverageRatio * 100 : coverageRatio;
  } else {
    percentage = 0;
  }
  
  const getStatus = () => {
    if (percentage >= 100) return { icon: ShieldCheck, color: 'success', label: 'Totalmente Coberto' };
    if (percentage >= 95) return { icon: Shield, color: 'warning', label: 'Cobertura Alta' };
    if (percentage >= 80) return { icon: ShieldAlert, color: 'warning', label: 'Cobertura Parcial' };
    return { icon: ShieldX, color: 'error', label: 'Cobertura Baixa' };
  };

  const status = getStatus();
  const Icon = status.icon;

  const sizeClasses = {
    sm: 'text-xs gap-1',
    md: 'text-sm gap-2',
    lg: 'text-base gap-2'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  if (!showDetails) {
    return (
      <Badge variant={status.color as 'success' | 'warning' | 'error'} className={`flex items-center ${sizeClasses[size]}`}>
        <Icon className={iconSizes[size]} />
        <span>{percentage.toFixed(1)}%</span>
      </Badge>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-surface-800/50 rounded-lg">
      <div className={`p-2 rounded-lg ${
        status.color === 'success' ? 'bg-green-500/20' :
        status.color === 'warning' ? 'bg-yellow-500/20' : 'bg-red-500/20'
      }`}>
        <Icon className={`w-5 h-5 ${
          status.color === 'success' ? 'text-green-400' :
          status.color === 'warning' ? 'text-yellow-400' : 'text-red-400'
        }`} />
      </div>
      <div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-surface-100">{percentage.toFixed(2)}%</span>
          <span className="text-surface-400 text-sm">{status.label}</span>
        </div>
        {lastVerified && (
          <p className="text-xs text-surface-500">
            Verificado: {new Date(lastVerified).toLocaleString('pt-BR')}
          </p>
        )}
      </div>
    </div>
  );
}
